import Grid from '@mui/material/Grid';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import type { CustomTargetingParams, CustomVariablesFormState } from '@openx/types/targeting/advancedTargeting';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { TargetingFields, type TargetingFieldsValues, type TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { type JSX, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingDropdownSection } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { DrawerTopBox } from '../shared/TargetingDrawer/DrawerTopBox';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { ContextualSegmentsDrawerTop } from './ContextualSegmentsDrawerTop';
import { CustomVariablesDrawerItems } from './CustomVariables/CustomVariablesDrawerItems';
import { CustomVariablesItems } from './CustomVariables/CustomVariablesItems';
import { CustomVariablesTopBox } from './CustomVariables/CustomVariablesTopBox/CustomVariablesTopBox';
import { useContextualSegmentsDrawerState, useCustomVariablesDrawerState } from './hooks/useDrawerState';
import { useGetContextualSegmentsComponents } from './hooks/useGetContextualSegmentsComponents';

type CustomTargetingType = typeof TargetingFields.CUSTOM_VARIABLES | typeof TargetingFields.CONTEXTUAL_SEGMENTS;

const targetingTypes: CustomTargetingType[] = [TargetingFields.CUSTOM_VARIABLES, TargetingFields.CONTEXTUAL_SEGMENTS];

const isTargetingType = (type?: string): type is CustomTargetingType =>
  targetingTypes.includes(type as CustomTargetingType);

type CustomTargetingWithContextualSegmentsProps = Omit<TargetingProps<CustomTargetingParams>, 'onFieldUpdate'> & {
  /** Called with the custom variables field (`field`) and the contextual segments field separately. */
  onFieldUpdate?: (
    field: TargetingFieldsValues,
    newData: CustomVariablesFormState | CustomVariablesDataPayload[] | null
  ) => void;
};

/**
 * Custom Targeting card with Custom Variables and Contextual Segments (V1/V2) sections, used while the
 * standalone contextual segments flag is off. Each section is stored in its own form field.
 */
export const CustomTargetingWithContextualSegments = memo(function CustomTargetingWithContextualSegments({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  accountUid = '',
}: CustomTargetingWithContextualSegmentsProps): JSX.Element {
  const { t } = useTranslation();
  const { ContextualSegmentsItems, segmentsV2, useAccountProviderId } = useGetContextualSegmentsComponents();

  const typeLabels: Record<CustomTargetingType, string> = {
    [TargetingFields.CUSTOM_VARIABLES]: t('Custom Variables'),
    [TargetingFields.CONTEXTUAL_SEGMENTS]: t('Contextual Segments'),
  };

  const { isDisabled: isCardDisabled, disabledReasonMessage: cardDisabledReasonMessage } = useTargetingDisabledState(
    TargetingFields.ADVANCED_TARGETING
  );
  const {
    isDisabled: isCustomDisabled,
    disabledReasonMessage: customDisabledReasonMessage,
    hasError: hasCustomError,
  } = useTargetingDisabledState(TargetingFields.CUSTOM_VARIABLES);
  const {
    isDisabled: isContextualDisabled,
    disabledReasonMessage: contextualDisabledReasonMessage,
    hasError: hasContextualError,
  } = useTargetingDisabledState(TargetingFields.CONTEXTUAL_SEGMENTS);

  const customVariables = useCustomVariablesDrawerState(targetingParams);
  const accountProviderId = useAccountProviderId(accountUid);
  const contextualSegments = useContextualSegmentsDrawerState(
    targetingParams?.contextual,
    segmentsV2,
    accountProviderId
  );

  const hasCustom = customVariables.initState.custom.length > 0;
  const hasContextual = Boolean(targetingParams?.contextual?.length);
  const optionsCount = [hasCustom, hasContextual].filter(Boolean).length;
  const { isOpen: isDropdownOpen, toggleOpen } = useDropdownOpen(optionsCount > 0);

  const [type, setType] = useState<CustomTargetingType | undefined>();

  const restoreParams = useCallback(() => {
    customVariables.restore();
    contextualSegments.restore();
  }, [customVariables.restore, contextualSegments.restore]);

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    // Only the edited field is saved, so the other one is not rewritten through the drawer state mapping
    onApply: () => {
      if (customVariables.isUpdated) onFieldUpdate(field, customVariables.fieldValue);
      if (contextualSegments.isUpdated)
        onFieldUpdate(TargetingFields.CONTEXTUAL_SEGMENTS, contextualSegments.fieldValue);
    },
    restoreParams,
  });

  const onOpenDrawerClick = useCallback(
    (selectedType?: string) => {
      // The fields can change outside the drawer (e.g. account change resets contextual segments).
      restoreParams();
      setType(
        isTargetingType(selectedType)
          ? selectedType
          : hasContextual
            ? TargetingFields.CONTEXTUAL_SEGMENTS
            : hasCustom
              ? TargetingFields.CUSTOM_VARIABLES
              : undefined
      );
      onDrawerOpen();
    },
    [hasContextual, hasCustom, onDrawerOpen, restoreParams]
  );

  const onRemoveCustom = useCallback(() => onFieldUpdate(field, null), [field, onFieldUpdate]);
  const onRemoveContextual = useCallback(() => onFieldUpdate(TargetingFields.CONTEXTUAL_SEGMENTS, []), [onFieldUpdate]);

  const customItems = <CustomVariablesItems readonly targetingParams={customVariables.initState} />;
  const contextualItems = (
    <ContextualSegmentsItems readonly targetingParams={contextualSegments.initState} accountUid={accountUid} />
  );

  return (
    <>
      {readonly ? (
        <TargetingReadOnly
          name={name}
          field={field}
          isEmpty={!optionsCount}
          onOpenDrawerClick={noop}
          readonly
          isDisabled={isCardDisabled}
          description={t(
            'Use custom and contextual data signals to create highly granular, rule-based targeting strategies.'
          )}
          disabledReasonMessage={cardDisabledReasonMessage}
          hasError={hasCustomError || hasContextualError}
        >
          {hasContextual && contextualItems}
          {hasCustom && customItems}
        </TargetingReadOnly>
      ) : (
        <TargetingReadOnly
          name={name}
          field={field}
          isEmpty={false}
          onOpenDrawerClick={onOpenDrawerClick}
          readonly={false}
          isDisabled={isCardDisabled}
          description={t(
            'Use custom and contextual data signals to create highly granular, rule-based targeting strategies.'
          )}
          optionsCount={optionsCount}
          isOpen={isDropdownOpen}
          toggleOpen={toggleOpen}
          disabledReasonMessage={cardDisabledReasonMessage}
          hasError={hasCustomError || hasContextualError}
        >
          <StyledReadOnlyTargetingDropdown>
            <TargetingDropdownSection
              name={typeLabels[TargetingFields.CUSTOM_VARIABLES]}
              isEmpty={!hasCustom}
              onOpenDrawerClick={() => onOpenDrawerClick(TargetingFields.CUSTOM_VARIABLES)}
              onRemove={onRemoveCustom}
              isDisabled={isCardDisabled || isCustomDisabled}
              disabledReasonMessage={cardDisabledReasonMessage ?? customDisabledReasonMessage}
              hasOptionsSelected={optionsCount > 0}
            >
              {hasCustom && customItems}
            </TargetingDropdownSection>

            <TargetingDropdownSection
              name={typeLabels[TargetingFields.CONTEXTUAL_SEGMENTS]}
              isEmpty={!hasContextual}
              onOpenDrawerClick={() => onOpenDrawerClick(TargetingFields.CONTEXTUAL_SEGMENTS)}
              onRemove={onRemoveContextual}
              isDisabled={isCardDisabled || isContextualDisabled}
              disabledReasonMessage={cardDisabledReasonMessage ?? contextualDisabledReasonMessage}
              hasOptionsSelected={optionsCount > 0}
            >
              {hasContextual && contextualItems}
            </TargetingDropdownSection>
          </StyledReadOnlyTargetingDropdown>
        </TargetingReadOnly>
      )}

      {isOpen && (
        <TargetingDrawer
          name={name}
          onClose={onDrawerClose}
          onApply={onDrawerApply}
          isApplyDisabled={
            contextualSegments.isIncomplete || (!customVariables.isUpdated && !contextualSegments.isUpdated)
          }
        >
          <DrawerTopBox>
            <Grid container sx={{ flexGrow: 1 }}>
              <Grid size={4}>
                <AutoComplete
                  textFieldProps={{ label: t('Targeting Type'), placeholder: t('[ Select Targeting Type ]') }}
                  disableClearable
                  options={targetingTypes}
                  fullWidth
                  renderOptions={{ dataTest: 'type-option', tooltip: true }}
                  getOptionLabel={option => typeLabels[option as CustomTargetingType] ?? option}
                  getOptionDisabled={option =>
                    isCardDisabled ||
                    (option === TargetingFields.CUSTOM_VARIABLES ? isCustomDisabled : isContextualDisabled)
                  }
                  onChange={(_, value) => setType(value as CustomTargetingType)}
                  value={type ?? ''}
                  textFieldReadOnly
                  data-test="select-targeting-type"
                />
              </Grid>

              {type === TargetingFields.CUSTOM_VARIABLES && (
                <CustomVariablesTopBox
                  items={customVariables.params}
                  onEnter={customVariables.onEnter}
                  sx={{ borderBottom: 0, paddingBottom: 0, paddingTop: 0 }}
                  besideTypeSelector
                />
              )}

              {type === TargetingFields.CONTEXTUAL_SEGMENTS && (
                <ContextualSegmentsDrawerTop
                  params={contextualSegments.params}
                  updateParams={contextualSegments.setParams}
                  onAdd={contextualSegments.onAdd}
                  accountUid={accountUid}
                  besideTypeSelector
                />
              )}
            </Grid>
          </DrawerTopBox>

          <CustomVariablesDrawerItems
            params={customVariables.params}
            updateParams={customVariables.setParams}
            showEmptyMessage={type === TargetingFields.CUSTOM_VARIABLES}
          />

          <ContextualSegmentsItems
            targetingParams={contextualSegments.params}
            readonly={false}
            onChange={contextualSegments.setParams}
            showEmptyMessage={type === TargetingFields.CONTEXTUAL_SEGMENTS}
            onAdd={contextualSegments.onAdd}
            accountUid={accountUid}
          />
        </TargetingDrawer>
      )}
    </>
  );
});
